import { useId, useState } from 'react';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState } from '@/components/DataState';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import {
  CAPTURED_VIA_OPTIONS,
  DEFAULT_CAPTURED_VIA,
  CaptureParseError,
  parseCaptureText,
  normaliseCapture,
  describeCaptureSummary,
  submitCapture,
  type CaptureEnvelope,
  type CaptureOutcome,
  type CaptureResponse,
} from '@/data/capture';
import { cn } from '@/lib/utils';

/* /sales/capture — Owner Capture (Outreach P0, amendment A7.1).
 *
 * A paste-JSON on-ramp: the owner (or Claude-in-Chrome, submitting on the
 * owner's behalf from a tab the owner opened on a practitioner's OWN site,
 * inside an isolated Chrome profile) pastes captured lead JSON here. It is
 * normalised client-side to {leads:[...]}, POSTed to
 * POST /v1/outreach/capture over the app's normal SESSION-COOKIE auth (the
 * same `api` client every other sales mutation uses — see data/capture.ts),
 * and the per-lead outcomes + summary render below.
 *
 * SAFETY INVARIANTS (P0 contract):
 *  - NO SEND anywhere on this page — capture only writes to the CRM via
 *    dedup/merge; nothing is emailed or messaged to a prospect.
 *  - NO HMAC/API key ever appears in this file or in the browser — the
 *    session cookie is the only credential, matching every other authed
 *    write in the app.
 *  - OWNER-ONLY: gated on workspace role === 'owner' (stricter than the
 *    owner-or-operator `canCreate`/`canManage` pattern used elsewhere in
 *    Sales/SEO, because P0 own-site-only enforcement is soft and explicitly
 *    scoped to the owner only — see planning-gate-outreach-cockpit A7.1).
 *  - Honest wording: outcomes are reported as created/merged/duplicate/
 *    rejected — never a blanket "success".
 */

const OUTCOME_TONE: Record<CaptureOutcome, Parameters<typeof Badge>[0]['tone']> = {
  created: 'success',
  merged: 'compliance',
  duplicate: 'neutral',
  rejected: 'danger',
};

const OUTCOME_LABEL: Record<CaptureOutcome, string> = {
  created: 'Created',
  merged: 'Merged',
  duplicate: 'Duplicate',
  rejected: 'Rejected',
};

const controlClass = cn(
  'w-full rounded-md border border-border-default bg-canvas px-3 text-sm outline-none',
  'focus-visible:border-ai focus-visible:ring-2 focus-visible:ring-ai/30 disabled:opacity-60',
);

const EXAMPLE_JSON = `{
  "business_name": "Cardiff Pilates Studio",
  "practitioner_name": "Jo Evans",
  "website": "https://cardiffpilates.example.com",
  "email": "hello@cardiffpilates.example.com",
  "city_region": "Cardiff",
  "services_category": "Pilates",
  "source_url": "https://cardiffpilates.example.com/contact"
}`;

/** Human label for a submitted row, echoed next to its outcome — never just
 *  a bare index, so the owner can tell which pasted lead got which result. */
function rowLabel(leadIndex: number, envelope: CaptureEnvelope | null): string {
  const lead = envelope?.leads[leadIndex];
  const name = lead?.business_name || lead?.practitioner_name || lead?.email || lead?.website;
  return name ? name : `Lead #${leadIndex + 1}`;
}

function submitErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return 'Owner Capture is owner-only — your session no longer has owner access to this workspace.';
    }
    if (err.status === 0) {
      return "Couldn't reach BookedCentral. Check your connection and try again — nothing was sent.";
    }
    return err.message || 'The capture could not be submitted. Please try again.';
  }
  return err instanceof Error ? err.message : 'Something went wrong submitting the capture.';
}

export function CapturePage() {
  const { activeWorkspace } = useAuth();
  const isOwner = activeWorkspace?.role === 'owner';

  const [raw, setRaw] = useState('');
  const [capturedVia, setCapturedVia] = useState<string>(DEFAULT_CAPTURED_VIA);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [submittedEnvelope, setSubmittedEnvelope] = useState<CaptureEnvelope | null>(null);
  const [response, setResponse] = useState<CaptureResponse | null>(null);

  const textareaId = useId();
  const selectId = useId();

  if (!isOwner) {
    return (
      <div>
        <PageHeader
          title="Owner Capture"
          subtitle="A paste-JSON on-ramp for owner-captured leads. No send — capture only."
        />
        <EmptyState
          title="Owner Capture is owner-only"
          teach="This P0 capture route writes directly into the shared CRM without automated own-site enforcement, so it's scoped to the workspace owner only. Ask the owner to submit captures, or use the Leads page's Add lead form."
        />
      </div>
    );
  }

  const handleSubmit = async () => {
    setParseError(null);
    setSubmitError(null);

    let envelope: CaptureEnvelope;
    try {
      const parsed = parseCaptureText(raw);
      envelope = normaliseCapture(parsed, capturedVia);
    } catch (err) {
      setParseError(err instanceof CaptureParseError ? err.message : 'Could not read the pasted JSON.');
      return;
    }

    setPending(true);
    setSubmittedEnvelope(envelope);
    try {
      const res = await submitCapture(envelope);
      setResponse(res);
    } catch (err) {
      setResponse(null);
      setSubmitError(submitErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Owner Capture"
        subtitle="Paste captured lead JSON — a single lead, an array, or {leads:[...]} — and it dedups straight into the CRM. No send: this only ever writes leads, never contacts anyone."
      />

      <Card className="p-4">
        <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
          <label className="block" htmlFor={selectId}>
            <span className="mb-1 block text-xs font-medium text-text-secondary">Captured via</span>
            <select
              id={selectId}
              value={capturedVia}
              disabled={pending}
              onChange={(e) => setCapturedVia(e.target.value)}
              className={cn(controlClass, 'h-control sm:w-64')}
            >
              {CAPTURED_VIA_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <p className="text-[11px] text-text-tertiary sm:pb-2">
            Used only for leads that don't already specify their own <code>captured_via</code>.
          </p>
        </div>

        <label className="mt-4 block" htmlFor={textareaId}>
          <span className="mb-1 block text-xs font-medium text-text-secondary">Lead JSON</span>
          <textarea
            id={textareaId}
            value={raw}
            disabled={pending}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={EXAMPLE_JSON}
            spellCheck={false}
            rows={14}
            className={cn(controlClass, 'font-mono text-xs leading-relaxed py-3')}
            aria-describedby={parseError ? `${textareaId}-error` : undefined}
          />
          <span className="mt-1 block text-[11px] text-text-tertiary">
            Accepted fields: practitioner_name, business_name, website, email, phone, instagram,
            linkedin, facebook, other_social, city_region, postcode, services_category,
            personalisation_note, source_directory, source_url, captured_via, notes — all strings,
            all optional except captured_via (defaulted above if omitted).
          </span>
        </label>

        {parseError && (
          <p id={`${textareaId}-error`} role="alert" className="mt-2 text-sm text-danger">
            {parseError}
          </p>
        )}

        <div className="mt-4 flex items-center gap-3">
          <Button variant="primary" onClick={() => void handleSubmit()} disabled={pending || !raw.trim()}>
            {pending ? 'Submitting…' : 'Submit capture'}
          </Button>
          {pending && (
            <span className="text-xs text-text-tertiary" role="status">
              Sending to your workspace CRM…
            </span>
          )}
        </div>
      </Card>

      {/* Results — aria-live so the owner (or Claude-in-Chrome reading the DOM)
       * gets the outcome without hunting for it after submit. */}
      <div className="mt-6" aria-live="polite">
        {submitError && (
          <Card className="border-danger/30 p-4">
            <p role="alert" className="text-sm text-danger">
              {submitError}
            </p>
          </Card>
        )}

        {response && (
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral">Source: POST /v1/outreach/capture</Badge>
              <span className="text-sm font-medium text-text-primary">
                {describeCaptureSummary(response.summary)}
              </span>
            </div>

            <div className="mt-4 overflow-hidden rounded-lg border border-border-subtle bg-surface">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse">
                  <thead>
                    <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                      <th className="px-3 py-2 font-semibold">Lead</th>
                      <th className="px-3 py-2 font-semibold">Outcome</th>
                      <th className="px-3 py-2 font-semibold">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {response.results.map((r, i) => (
                      <tr key={i} className="border-b border-border-subtle last:border-0">
                        <td className="px-3 py-2.5 text-sm text-text-primary">
                          {rowLabel(i, submittedEnvelope)}
                        </td>
                        <td className="px-3 py-2.5">
                          <Badge tone={OUTCOME_TONE[r.outcome]}>{OUTCOME_LABEL[r.outcome]}</Badge>
                        </td>
                        <td className="px-3 py-2.5 text-sm text-text-secondary">
                          {r.lead_id != null ? `Lead #${r.lead_id}` : ''}
                          {r.lead_id != null && r.reason ? ' · ' : ''}
                          {r.reason || (r.lead_id == null ? '—' : '')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        )}
      </div>

      <p className="mt-3 text-[11px] text-text-tertiary">
        Capture never sends anything to a prospect — it only writes to your workspace CRM, with
        dedup (email, then domain) and provenance recorded on every lead. Own-site-only sourcing on
        the Claude-in-Chrome route is enforced by the owner, not this page.
      </p>
    </div>
  );
}
